import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import ReviewPage from "./ReviewPage";
import CustomButton from "../Common/Button";
import { GetExamQuestion } from "../../utils/axios/questionAPI";
import { SubmitAnswers } from "../../utils/axios/questionAPI";
import moment from "moment";
import { GetUserInfo } from "../../utils/helper";

const getEndTime = () => {
  const storedStartTime = localStorage.getItem("startTime");
  if (!storedStartTime) {
    const newStartTime = Date.now();
    localStorage.setItem("startTime", newStartTime.toString());
    return newStartTime + 1800 * 1000; // 30 minutes in milliseconds
  }
  return Number(storedStartTime) + 1800 * 1000;
};

const calculateSecondsLeft = (endTime) =>
  Math.floor((endTime - Date.now()) / 1000);

function Question() {
  const [userInfo] = useState(GetUserInfo());
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const questionsRef = useRef([]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(
    calculateSecondsLeft(getEndTime())
  );
  const navigate = useNavigate();
  const warningCount = useRef(0);
  const isSubmitting = useRef(false);
  const lastFullscreenExitTime = useRef(0);

  useEffect(() => {
    localStorage.setItem("startTime", Date.now().toString()); // Initialize the start time
    setSecondsLeft(calculateSecondsLeft(getEndTime())); // Correct immediately
    sessionStorage.removeItem("isSubmitting");
    const intervalId = setInterval(async () => {
      const newSecondsLeft = calculateSecondsLeft(getEndTime());
      setSecondsLeft(newSecondsLeft);

      if (newSecondsLeft <= 0) {
        clearInterval(intervalId);
        localStorage.removeItem("startTime"); // Remove the start time
        submitExam();

        // navigate('/');
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [navigate]);

  const submitExam = async (isMalpractice = false) => {
    isSubmitting.current = true;
    let questionId = questionsRef.current.map((item) => item.QuestionId);

    // let optionId = selectedAnswers.map(item => {return item.OptionId == null ? 0 : item.OptionId});
    let x = sessionStorage.getItem("SelectedAnswers");
    let parsedX = x ? JSON.parse(x) : Array(questionsRef.current.length).fill(0);
    let optionId = Array.from(parsedX, (item) => item?.OptionId || 0);
    let examId = sessionStorage.getItem("ExamId");
    let examPayload = {
      ExamId: parseInt(examId),
      QuestionId: questionId,
      OptionId: optionId,
      EndTime: moment().format("YYYY-MM-DD HH:mm:ss"),
      CreatedBy: userInfo?.[0]?.UserId || 0,
    };

    let submitExamRespone = await SubmitAnswers(examPayload);
    if (submitExamRespone.success) {
      sessionStorage.removeItem("userInfo");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("ExamId");

      if (!isMalpractice) {
        await Swal.fire({ icon: 'success', title: 'Session Ended', text: 'Your test has been successfully submitted.' });
      }
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => { });
      }
      navigate("/thankyou");
    }
  };

  useEffect(() => {
    const handleLeave = (forceWarning) => {
      if (isSubmitting.current) return;

      setTimeout(() => {
        const isForce = forceWarning === true;

        let isMalpractice = false;
        if (document.hidden || isForce) {
          isMalpractice = true;
        } else if (!document.hasFocus()) {
          // If we recently exited full screen (within last 500ms), 
          // this blur is likely caused by the exit action itself. Ignore it.
          if (Date.now() - lastFullscreenExitTime.current < 500) {
            return;
          }
          isMalpractice = true;
        }

        if (isMalpractice) {
          if (warningCount.current === 0) {
            warningCount.current = 1;
            Swal.fire({
              icon: 'warning',
              title: 'Warning 1!',
              text: "You are trying to cheat us. Don't be too smart! Stay in our site or else your test will be automatically submitted.",
              confirmButtonText: 'I understand',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          } else if (warningCount.current === 1) {
            warningCount.current = 2;
            Swal.fire({
              icon: 'warning',
              title: 'Warning 2!',
              text: "This is your final warning. Do not leave the site or your test will be automatically submitted due to malpractice.",
              confirmButtonText: 'I understand',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          } else if (warningCount.current === 2) {
            warningCount.current = 3;
            Swal.fire({
              icon: 'warning',
              title: 'Test Submitted',
              text: "Test submitted automatically due to malpractice.",
              showConfirmButton: false,
              timer: 3000
            });
            submitExam(true);
          }
        }
      }, 100);
    };

    const trapFullscreen = () => {
      if (!document.fullscreenElement && !isSubmitting.current) {
        document.documentElement.requestFullscreen({ navigationUI: "hide" }).then(() => {
          if (navigator.keyboard && navigator.keyboard.lock) {
            navigator.keyboard.lock(); // Lock all system keys
          }
          const overlay = document.getElementById('fullscreen-enforcer');
          if (overlay) overlay.remove();
        }).catch(() => {
          let overlay = document.getElementById('fullscreen-enforcer');
          if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'fullscreen-enforcer';
            Object.assign(overlay.style, {
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 1050, display: 'flex',
              flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
              color: 'white', cursor: 'pointer'
            });
            overlay.innerHTML = `
               <h1 style="color:#ff4444; font-size:2.5rem; margin-bottom:15px; text-align:center;">⚠️ Fullscreen Mode Required</h1>
               <p style="font-size:1.2rem; text-align:center; max-width:600px; line-height:1.5; margin-bottom:30px; color:#e0e0e0;">
                 You have exited fullscreen mode. Active monitoring remains enabled. Accessing other tabs or applications will be flagged as malpractice.
               </p>
               <p style="font-size:1.2rem; font-weight:bold; padding:12px 24px; border:2px solid white; border-radius:8px; background:rgba(255,255,255,0.1);">
                 Click anywhere to resume exam
               </p>
             `;

            overlay.onclick = () => {
              document.documentElement.requestFullscreen({ navigationUI: "hide" }).then(() => {
                if (navigator.keyboard && navigator.keyboard.lock) navigator.keyboard.lock();
                overlay.remove();
              }).catch(() => { });
            };
            document.body.appendChild(overlay);
          }
        });
      }
    };

    const handleFullscreenChange = () => {
      if (isSubmitting.current || sessionStorage.getItem("isSubmitting") === "true") return;
      if (!document.fullscreenElement) {
        lastFullscreenExitTime.current = Date.now();
        trapFullscreen();
      }
    };

    const handleMouseLeave = (e) => {
      // Ignore if mouse leaves through the top edge (e.clientY <= 5) to avoid penalizing accidental hovers near browser UI
      if (e.clientX <= 0 || (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight)) {
        handleLeave(true);
      }
    };

    document.addEventListener("visibilitychange", handleLeave);
    window.addEventListener("blur", handleLeave);
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'F11' || e.keyCode === 27 || e.keyCode === 122) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // Trigger native browser popup
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleLeave);
      window.removeEventListener("blur", handleLeave);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const overlay = document.getElementById('fullscreen-enforcer');
      if (overlay) overlay.remove();
    };
  }, []);

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      const progress =
        ((currentQuestionIndex - 1) / (questions.length - 1)) * 100;
      setProgress(progress);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      const progress =
        ((currentQuestionIndex + 1) / (questions.length - 1)) * 100;
      setProgress(progress);
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  const handleReviewAndSubmit = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    getQuestions();
  }, []);

  useEffect(() => {
    const handleBackButtonEvent = (e) => {
      e.preventDefault();
      window.history.pushState(null, null, window.location.pathname);

      if (!window.confirm("Are you sure you want to exit?")) {
        window.history.pushState(null, null, window.location.pathname);
      } else {
        navigate("/");
      }
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener("popstate", handleBackButtonEvent);

    return () => {
      window.removeEventListener("popstate", handleBackButtonEvent);
    };
  }, [navigate]);

  const [selectedAnswers, setSelectedAnswers] = useState([]);

  let question;
  const handleOptionChange = (selectedOption) => {
    const newSelectedAnswers = [...selectedAnswers];
    newSelectedAnswers[currentQuestionIndex] = selectedOption;
    setSelectedAnswers(newSelectedAnswers);
    sessionStorage.setItem(
      "SelectedAnswers",
      JSON.stringify(newSelectedAnswers)
    );
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "999",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "5px",
    padding: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.3)",
    maxWidth: "100%",
    overflow: "auto",
  };

  const [progress, setProgress] = useState(0);
  const shuffleArray = (array) => {
    if (!Array.isArray(array)) return array;
    let shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const normalizeOptions = (q) => {
    if (Array.isArray(q.Options)) return q.Options;
    if (typeof q.Options === "string" && q.Options.trim() !== "") {
      return q.Options.split(",").map((optStr, idx) => ({
        OptionId: idx + 1,
        Options: optStr.trim(),
      }));
    }
    return [];
  };

  const getQuestions = async () => {
    try {
      const preloadImage = new Promise((resolve) => {
        const img = new Image();
        img.src = require("../../assets/images/Question-New.webp");
        img.onload = resolve;
        img.onerror = resolve; // Fallback so we don't freeze if image fails
      });

      const [resBasicApti, resSqlBasics, resHardOops, resHardApti] = await Promise.all([
        GetExamQuestion({ CategoryId: 1, LimitCount: 50 }),
        GetExamQuestion({ CategoryId: 2, LimitCount: 50 }),
        GetExamQuestion({ CategoryId: 5, LimitCount: 50 }), // Hard OOPS
        GetExamQuestion({ CategoryId: 6, LimitCount: 50 }), // Hard Apti
        preloadImage // Wait for image to load alongside the APIs!
      ]);

      const basicAptiQuestions = shuffleArray(Array.isArray(resBasicApti?.data) ? resBasicApti.data : []).slice(0, 15);
      const sqlBasicsQuestions = shuffleArray(Array.isArray(resSqlBasics?.data) ? resSqlBasics.data : []).slice(0, 13);
      const hardOopsQuestions = shuffleArray(Array.isArray(resHardOops?.data) ? resHardOops.data : []).slice(0, 7);
      const hardAptiQuestions = shuffleArray(Array.isArray(resHardApti?.data) ? resHardApti.data : []).slice(0, 5);

      const final40Questions = [...basicAptiQuestions, ...hardAptiQuestions, ...sqlBasicsQuestions, ...hardOopsQuestions]
        .map(q => ({ ...q, Options: normalizeOptions(q) }));

      setQuestions(final40Questions);
      question = final40Questions;
      setSelectedAnswers(Array(final40Questions.length).fill(0));
    } catch (error) {
      console.error("Error fetching exam questions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(255,255,255,1)", zIndex: 99999, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <img src={require("../../assets/images/Evalutech.png")} alt="Evalutech Logo" style={{ height: "6rem", marginBottom: "20px" }} />
          <h2 style={{ color: "#F59E0B" }}>Loading Exam...</h2>
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          fontFamily: "Arial, sans-serif",
          height: "100vh",
          backgroundColor: "white",
        }}
      >
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "100%",
            backgroundColor: "#ffffff",
          }}
        >
          <div style={{ position: "absolute", top: "5px", left: "10px", zIndex: 10 }}>
            <img
              src={require("../../assets/images/Evalutech.png")}
              alt="Evalutech Logo"
              style={{ height: "4.5rem" }}
            />
          </div>
          <img
            src={require("../../assets/images/Question-New.webp")}
            alt="Time GIF"
            style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "left center" }}
          />
        </div>
        <div
          style={{
            flex: 0.7,
            paddingLeft: "clamp(20px, 4vw, 60px)",
            paddingRight: "clamp(15px, 3vw, 40px)",
            maxWidth: "clamp(450px, 45vw, 900px)",
            height: "100vh",
            overflowY: "auto",
            boxSizing: "border-box",
            position: "relative",
            paddingBottom: "50px",
          }}
        >
          <div
            style={{
              padding: "clamp(10px, 1.5vh, 30px) 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <img
              src={require("../../assets/images/swt-logo.png")}
              alt="Sunware Logo"
              style={{ width: "clamp(60px, 8vw, 120px)", marginBottom: "8px" }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src={require("../../assets/images/Animation - 1699939128035.gif")}
                alt="Sample GIF"
                style={{ width: "clamp(25px, 3.5vh, 60px)", height: "clamp(25px, 3.5vh, 60px)" }}
              />
              <h2 style={{ margin: 0, fontSize: "clamp(16px, min(2vw, 3vh), 28px)" }}>{formatTime(secondsLeft)}</h2>
            </div>
            <div
              style={{
                width: "100%",
                height: "10px",
                backgroundColor: "gray",
                borderRadius: "5px",
                marginTop: "10px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor: "#007BFF",
                  borderRadius: "5px",
                }}
              ></div>
              {progress >= 0 && (
                <div
                  style={{
                    position: "absolute",
                    left: `${Math.min(progress, 94)}%`,
                    top: "15px",
                    textAlign: "center",
                    fontSize: "clamp(10px, 1vw, 16px)",
                  }}
                >
                  ({progress.toFixed(0)}%)
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: "clamp(5px, 1vh, 20px) 0" }}>
            {questions?.length && (
              <p style={{ marginBottom: "clamp(10px, 1.5vh, 25px)", fontSize: "clamp(13px, min(1.5vw, 2.5vh), 22px)", lineHeight: "1.5" }}>
                {currentQuestionIndex + 1}.{" "}
                {questions[currentQuestionIndex].Questions}
              </p>
            )}
            <div>
              {Array.isArray(questions[currentQuestionIndex]?.Options) &&
                questions[currentQuestionIndex].Options.map((option, index) => (
                  <div key={index} style={{ marginBottom: "clamp(6px, 1vh, 15px)" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "clamp(6px, 1vh, 16px) clamp(8px, 1.5vw, 16px)",
                        border: "1px solid #007BFF",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "clamp(12px, min(1.2vw, 2.2vh), 20px)",
                        backgroundColor:
                          selectedAnswers[currentQuestionIndex]?.Options ===
                            option.Options
                            ? "#C4D6EE"
                            : "white",
                      }}
                    >
                      <input
                        type="radio"
                        name={`answer-${questions[currentQuestionIndex].id}`}
                        value={option.OptionId}
                        style={{ marginRight: "10px", transform: "scale(1.2)" }}
                        checked={
                          selectedAnswers[currentQuestionIndex]?.Options ===
                          option.Options
                        }
                        onChange={() => handleOptionChange(option)}
                      />
                      {option.Options}
                    </label>
                  </div>
                ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingBottom: "20px" }}>
            {currentQuestionIndex > 0 && (
              <CustomButton text="Previous" onClick={handlePreviousQuestion} />
            )}
            {currentQuestionIndex < questions.length - 1 ? (
              <CustomButton text="Next" onClick={handleNextQuestion} />
            ) : (
              <CustomButton text="Review" onClick={handleReviewAndSubmit} />
            )}
          </div>
          <div style={{ position: "absolute", bottom: "10px", right: "20px", fontSize: "12px", color: "#999" }}>
            © 2026 Sunware Technologies. All rights reserved.
          </div>
          {isOpen && (
            <div style={modalOverlayStyle}>
              <div style={modalStyle}>
                <ReviewPage
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  questions={questions}
                  selectedAnswers={selectedAnswers}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Question;
